import { isObject } from 'lodash'

import * as beatActions from './beats'
import * as bookActions from './books'
import * as cardActions from './cards'
import * as categoryActions from './categories'
import * as characterActions from './characters'
import * as customAttributeActions from './customAttributes'
import * as imageActions from './images'
import * as audioClipActions from './audioClips'
import * as lineActions from './lines'
import * as noteActions from './notes'
import * as placeActions from './places'
import * as seriesActions from './series'
import * as tagActions from './tags'
import * as uiActions from './ui'
import * as hierarchyActions from './hierarchy'
import * as featureFlagsActions from './featureFlags'
import * as errorActions from './error'
import * as projectActions from './project'
import * as clientActions from './client'
import * as licenseActions from './license'
import * as knownFilesActions from './knownFiles'
import * as templatesActions from './templates'
import * as settingsActions from './settings'
import * as backupsActions from './backups'
import * as applicationStateActions from './applicationState'
import * as imageCacheActions from './imageCache'
import * as notificationsActions from './notifications'
import * as domEventsActions from './domEvents'
import * as testingAndDiagnosisActions from './testingAndDiagnosis'
import * as attributesActions from './attributes'
import * as undoActions from './undo'
import * as offlineActions from './offline'
import * as accountActions from './account'
import * as familyTreesActions from './familyTree'
import * as ideasActions from './ideas'
import * as audioCacheActions from './audioCache'

/**
 * @typedef {import('../index').PlottrState} PlottrState
 */

/**
 * NOTE(ed): All objects that can be dispatched *must* be defined as
 * the return type of a non-thunk function.  You must then
 * `omit<object, name>` the thunk functions from each collection of
 * actions because these types are only for the objects that
 * eventually make it to the reducers.
 *
 * @typedef {import('./account').AccountActions} AccountActions
 * @typedef {import('./beats').BeatActions} BeatActions
 * @typedef {import('./audioCache').AudioCacheActions} AudioCacheActions
 * @typedef {import('./ideas').IdeasActions} IdeasActions
 * @typedef {import('./familyTree').FamilyTreeActions} FamilyTreeActions
 * @typedef {import('./offline').OfflineActions} OfflineActions
 * @typedef {import('./undo').UndoActions} UndoActions
 * @typedef {import('./attributes').AttributesActions} AttributesActions
 * @typedef {import('./testingAndDiagnosis').TestingAndDiagnosisActions} TestingAndDiagnosisActions
 * @typedef {import('./domEvents').DomEventsActions} DomEventsActions
 * @typedef {import('./notifications').NotificationsActions} NotificationsActions
 * @typedef {import('./imageCache').ImageCacheActions} ImageCacheActions
 * @typedef {import('./applicationState').ApplicationStateActions} ApplicationStateActions
 * @typedef {import('./backups').BackupsActions} BackupsActions
 * @typedef {import('./settings').SettingsActions} SettingsActions
 * @typedef {import('./templates').TemplatesActions} TemplatesActions
 * @typedef {import('./knownFiles').KnownFilesActions} KnownFilesActions
 * @typedef {import('./license').LicenseActions} LicenseActions
 * @typedef {import('./client').ClientActions} ClientActions
 * @typedef {import('./project').ProjectActions} ProjectActions
 * @typedef {import('./error').ErrorActions} ErrorActions
 * @typedef {import('./featureFlags').FeatureFlagsActions} FeatureFlagsActions
 * @typedef {import('./hierarchy').HierarchyActions} HierarchyActions
 * @typedef {import('./ui').UiActions} UiActions
 * @typedef {import('./tags').TagsActions} TagsActions
 * @typedef {import('./series').SeriesActions} SeriesActions
 * @typedef {import('./places').PlacesActions} PlacesActions
 * @typedef {import('./notes').NotesActions} NotesActions
 * @typedef {import('./lines').LinesActions} LinesActions
 * @typedef {import('./audioClips').AudioClipsActions} AudioClipsActions
 * @typedef {import('./images').ImagesActions} ImagesActions
 * @typedef {import('./customAttributes').CustomAttributesActions} CustomAttributesActions
 * @typedef {import('./characters').CharactersActions} CharactersActions
 * @typedef {import('./categories').CategoriesActions} CategoriesActions
 * @typedef {import('./cards').CardsActions} CardsActions
 * @typedef {import('./books').BooksActions} BooksActions
 *
 * @typedef { AccountActions | BeatActions | AudioCacheActions | IdeasActions | FamilyTreeActions | OfflineActions | UndoActions | AttributesActions | TestingAndDiagnosisActions | DomEventsActions | NotificationsActions | ImageCacheActions | ApplicationStateActions | BackupsActions | SettingsActions | TemplatesActions | KnownFilesActions | LicenseActions | ClientActions | ProjectActions | ErrorActions | FeatureFlagsActions | HierarchyActions | UiActions | TagsActions | SeriesActions | PlacesActions | NotesActions | LinesActions | AudioClipsActions | ImagesActions | CustomAttributesActions | CharactersActions | CategoriesActions | CardsActions | BooksActions } PltrAction
 */

/**
 * @param {function(any): PlottrState} selectState
 */
const actions = (selectState) => {
  function thunkActionObjectUntilAction(action) {
    // If the action says it was curried, we need to keep thunking
    // until we get to the redux thunk args.
    if (action.curried > 0) {
      return (...args) => {
        return thunkActionObjectUntilAction({
          curried: action.curried - 1,
          action: action.action(...args),
        })
      }
    } else {
      // When we hit zero, this has to be a thunk action.
      return (dispatch, getState) => {
        const augmentedGetState = () => {
          return selectState(getState())
        }
        return action.action(dispatch, augmentedGetState)
      }
    }
  }

  function thunkUntilAction(action) {
    // If it's an object, it uses the new convention of specifying
    // curried depth.
    if (isObject(action) && typeof action !== 'function') {
      return thunkActionObjectUntilAction(action)
    } else {
      // Otherwise, we accomodate at most a depth of one in the
      // curried chain.
      const actionFunction = action
      return (...actionArgs) => {
        const applied = actionFunction(...actionArgs)
        if (typeof applied === 'function') {
          return (dispatch, getState) => {
            const augmentedGetState = () => {
              return selectState(getState())
            }
            return applied(dispatch, augmentedGetState)
          }
        } else {
          return applied
        }
      }
    }
  }

  const wiredActions = (actions) => {
    return Object.entries(actions).reduce((actionGroupAcc, nextEntry) => {
      const [actionName, action] = nextEntry
      // Ignore the default export
      if (actionName === 'default') {
        return actionGroupAcc
      } else {
        return {
          ...actionGroupAcc,
          [actionName]: thunkUntilAction(action),
        }
      }
    }, {})
  }

  return Object.entries({
    beat: beatActions,
    book: bookActions,
    card: cardActions,
    category: categoryActions,
    character: characterActions,
    customAttribute: customAttributeActions,
    image: imageActions,
    audioClips: audioClipActions,
    line: lineActions,
    note: noteActions,
    place: placeActions,
    series: seriesActions,
    tag: tagActions,
    ui: uiActions,
    hierarchyLevels: hierarchyActions,
    featureFlags: featureFlagsActions,
    error: errorActions,
    project: projectActions,
    client: clientActions,
    license: licenseActions,
    knownFiles: knownFilesActions,
    templates: templatesActions,
    settings: settingsActions,
    backups: backupsActions,
    applicationState: applicationStateActions,
    imageCache: imageCacheActions,
    notifications: notificationsActions,
    domEvents: domEventsActions,
    testingAndDiagnosis: testingAndDiagnosisActions,
    attributes: attributesActions,
    undo: undoActions,
    offline: offlineActions,
    account: accountActions,
    familyTrees: familyTreesActions,
    ideas: ideasActions,
    audioCache: audioCacheActions,
  }).reduce(
    (actionsAcc, nextEntry) => {
      const [name, actionBundle] = nextEntry
      return {
        ...actionsAcc,
        [name]: wiredActions(actionBundle),
      }
    },
    {
      beat: beatActions,
      book: bookActions,
      card: cardActions,
      category: categoryActions,
      character: characterActions,
      customAttribute: customAttributeActions,
      image: imageActions,
      audioClips: audioClipActions,
      line: lineActions,
      note: noteActions,
      place: placeActions,
      series: seriesActions,
      tag: tagActions,
      ui: uiActions,
      hierarchyLevels: hierarchyActions,
      featureFlags: featureFlagsActions,
      error: errorActions,
      project: projectActions,
      client: clientActions,
      license: licenseActions,
      knownFiles: knownFilesActions,
      templates: templatesActions,
      settings: settingsActions,
      backups: backupsActions,
      applicationState: applicationStateActions,
      imageCache: imageCacheActions,
      notifications: notificationsActions,
      domEvents: domEventsActions,
      testingAndDiagnosis: testingAndDiagnosisActions,
      attributes: attributesActions,
      undo: undoActions,
      offline: offlineActions,
      account: accountActions,
      familyTrees: familyTreesActions,
      pinBoard: uiActions,
      ideas: ideasActions,
      audioCache: audioCacheActions,
    }
  )
}

export default actions

export {
  beatActions,
  bookActions,
  cardActions,
  categoryActions,
  characterActions,
  customAttributeActions,
  imageActions,
  audioClipActions,
  lineActions,
  noteActions,
  placeActions,
  seriesActions,
  tagActions,
  uiActions,
  hierarchyActions,
  featureFlagsActions,
  errorActions,
  projectActions,
  clientActions,
  licenseActions,
  knownFilesActions,
  templatesActions,
  settingsActions,
  backupsActions,
  applicationStateActions,
  imageCacheActions,
  notificationsActions,
  domEventsActions,
  testingAndDiagnosisActions,
  attributesActions,
  undoActions,
  offlineActions,
  accountActions,
  familyTreesActions,
  ideasActions,
  audioCacheActions,
}
